import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

export interface ConfirmOpts {
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

const ConfirmContext = createContext<((opts: ConfirmOpts) => Promise<boolean>) | null>(null);

export function useConfirm(): (opts: ConfirmOpts) => Promise<boolean> {
  const fn = useContext(ConfirmContext);
  if (!fn) throw new Error("useConfirm: missing <ConfirmProvider>");
  return fn;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ opts: ConfirmOpts; resolve: (v: boolean) => void } | null>(null);

  const confirm = useCallback(
    (opts: ConfirmOpts) => new Promise<boolean>((resolve) => setState({ opts, resolve })),
    []
  );

  const close = useCallback((value: boolean) => {
    if (state) {
      state.resolve(value);
      setState(null);
    }
  }, [state]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && <ConfirmDialog opts={state.opts} onClose={close} />}
    </ConfirmContext.Provider>
  );
}

function ConfirmDialog({ opts, onClose }: { opts: ConfirmOpts; onClose: (v: boolean) => void }) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(false); }
      else if (e.key === "Enter") { e.preventDefault(); onClose(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center p-4"
      style={{ background: "var(--backdrop)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      onClick={() => onClose(false)}
    >
      <div className="card w-full max-w-sm p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h2 id="confirm-title" className="text-lg font-semibold">{opts.title}</h2>
        {opts.description && (
          <div className="mt-2 text-sm muted">{opts.description}</div>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button className="btn" type="button" onClick={() => onClose(false)}>
            {opts.cancelLabel ?? "Cancel"}
          </button>
          <button
            ref={confirmRef}
            type="button"
            className={opts.danger ? "btn-danger" : "btn-primary"}
            onClick={() => onClose(true)}
          >
            {opts.confirmLabel ?? "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}
